import { useEffect, useState } from 'react';
import { BookOpen, Loader2, Pencil, Plus, Archive, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { repo, RECIPE_KINDS, RECIPE_KIND_LABELS, type Recipe, type RecipeInput, type RecipeKind } from '@/lib/repo';

// The CONTEXT REPOSITORY ("the recipe"): CNN's standing editorial knowledge —
// playbooks, editing specs, brand/style/business rules, graded examples, and the
// performance data behind the guidance. Org-wide, human-curated, versioned. At
// produce time the matching recipes GROUND the Story Engine's cut (see the
// "Grounded by Context Repository" toggle on a topic's produce controls).

const inputClass =
  'w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const labelClass = 'block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground';

function csv(v: string): string[] {
  return v
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

interface FormState {
  recipe_id?: string;
  kind: RecipeKind;
  title: string;
  body: string;
  genres: string;
  aspects: string;
  tags: string;
}

const EMPTY_FORM: FormState = { kind: 'playbook', title: '', body: '', genres: '', aspects: '', tags: '' };

export default function ContextRepository() {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    setError(null);
    try {
      const { recipes } = await repo.listRecipes();
      setRecipes(recipes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recipes');
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function edit(r: Recipe) {
    setForm({
      recipe_id: r.recipe_id,
      kind: r.kind,
      title: r.title,
      body: r.body,
      genres: r.applies_to.genres.join(', '),
      aspects: r.applies_to.aspects.join(', '),
      tags: r.tags.join(', '),
    });
  }

  async function save() {
    if (!form) return;
    if (!form.title.trim() || !form.body.trim()) {
      setError('Title and body are required.');
      return;
    }
    setBusy(true);
    setError(null);
    const input: RecipeInput = {
      kind: form.kind,
      title: form.title,
      body: form.body,
      applies_to: { genres: csv(form.genres), aspects: csv(form.aspects), source_conditions: [] },
      tags: csv(form.tags),
    };
    try {
      if (form.recipe_id) await repo.updateRecipe(form.recipe_id, input);
      else await repo.createRecipe(input);
      setForm(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save recipe');
    } finally {
      setBusy(false);
    }
  }

  async function archive(r: Recipe) {
    if (!confirm(`Archive "${r.title}"? It will stop grounding new productions.`)) return;
    setBusy(true);
    try {
      await repo.archiveRecipe(r.recipe_id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive');
    } finally {
      setBusy(false);
    }
  }

  const byKind = RECIPE_KINDS.map((k) => ({ kind: k, items: recipes.filter((r) => r.kind === k) })).filter(
    (g) => g.items.length > 0,
  );

  return (
    <div className="container mx-auto max-w-6xl px-6 py-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Context Repository</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            CNN&apos;s standing editorial knowledge — &ldquo;the recipe.&rdquo; Playbooks, editing specs, brand/style/business
            rules, graded examples, and performance data. These ground every production: at produce time the recipes that match
            the chosen genre &amp; aspect are composed into the brief and handed to the Story Engine, and recorded on the output
            for traceability.
          </p>
        </div>
        {!form && (
          <Button size="sm" onClick={() => setForm({ ...EMPTY_FORM })}>
            <Plus className="mr-1 h-4 w-4" />
            New recipe
          </Button>
        )}
      </div>

      {error && <div className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-600">{error}</div>}

      {form && (
        <div className="mt-5 rounded-lg border bg-card p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold">{form.recipe_id ? 'Edit recipe' : 'New recipe'}</h2>
            <button onClick={() => setForm(null)} className="text-muted-foreground hover:text-foreground" title="Cancel">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Kind</label>
              <select
                className={inputClass}
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as RecipeKind })}
              >
                {RECIPE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {RECIPE_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Title</label>
              <input
                className={inputClass}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Vertical Video Playbook"
              />
            </div>
          </div>
          <div className="mt-3">
            <label className={labelClass}>Guidance (markdown)</label>
            <textarea
              className={`${inputClass} min-h-[180px] font-mono`}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="The machine-usable guidance the engine should follow…"
            />
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <div>
              <label className={labelClass}>Genres (blank = all)</label>
              <input
                className={inputClass}
                value={form.genres}
                onChange={(e) => setForm({ ...form, genres: e.target.value })}
                placeholder="cnn_social, vertical_reframe"
              />
            </div>
            <div>
              <label className={labelClass}>Aspects (blank = all)</label>
              <input
                className={inputClass}
                value={form.aspects}
                onChange={(e) => setForm({ ...form, aspects: e.target.value })}
                placeholder="9:16"
              />
            </div>
            <div>
              <label className={labelClass}>Tags</label>
              <input
                className={inputClass}
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="verticals, standards"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button size="sm" onClick={save} disabled={busy}>
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              {form.recipe_id ? 'Save new version' : 'Create recipe'}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setForm(null)} disabled={busy}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {!loaded ? (
        <div className="py-16 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : recipes.length === 0 ? (
        <div className="mt-6 rounded-lg border bg-card py-12 text-center">
          <BookOpen className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            The Context Repository is empty. Add the guidance a production should follow — start with the Vertical Video Playbook
            and Editing Spec.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {byKind.map((group) => (
            <div key={group.kind}>
              <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {RECIPE_KIND_LABELS[group.kind]}
              </h2>
              <div className="grid gap-3 lg:grid-cols-2">
                {group.items.map((r) => (
                  <div key={r.recipe_id} className="rounded-lg border bg-card p-4 shadow-sm">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-sm font-medium">{r.title}</span>
                      <Badge variant="secondary">v{r.version}</Badge>
                      <div className="ml-auto flex items-center gap-1">
                        <button
                          onClick={() => edit(r)}
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                          title="Edit (creates a new version)"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => archive(r)}
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                          title="Archive"
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="line-clamp-3 whitespace-pre-wrap text-xs text-muted-foreground">{r.body}</p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {r.applies_to.genres.length === 0 && r.applies_to.aspects.length === 0 ? (
                        <Badge variant="info">applies to all</Badge>
                      ) : (
                        <>
                          {r.applies_to.genres.map((g) => (
                            <Badge key={`g-${g}`} variant="info">
                              {g}
                            </Badge>
                          ))}
                          {r.applies_to.aspects.map((a) => (
                            <Badge key={`a-${a}`} variant="secondary">
                              {a}
                            </Badge>
                          ))}
                        </>
                      )}
                      {r.tags.map((t) => (
                        <span key={`t-${t}`} className="text-[11px] text-muted-foreground">
                          #{t}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
